import { useEffect, useState, useRef } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Modal,
  Input,
  Row,
  Col,
  Badge,
  Button,
  Spinner,
} from 'reactstrap';
import { motion, AnimatePresence } from 'framer-motion';
import FeatherIcon from 'feather-icons-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import * as XLSX from 'xlsx';
import {
  formatCreatedDate,
  formatDateTimeForAPI,
  workstatusOptions,
} from '../../constants/utils';
import AssetUsageStatusFields from '../../components/AssetUsageStatusFields';
import api from '../../helpers/api';
import WorkOrderDetailsModal from '../../components/WorkOrderDetailsModal';
import FileModal from '../../components/Common/importExportModal';
import {
  uploadServiceContractFiles,
  validateFiles,
} from '../../helpers/fileUpload';

// Import new hooks and components
import {
  useReferenceData,
  usePagination,
  useNotification,
  useModalState,
} from '../../Hooks/index.js';
import {
  NotificationAlert,
  LoadMoreButton,
  ConfirmDeleteModal,
} from '../../components/Common/index.js';
import { useTranslation } from 'react-i18next';

const CalibrationDetail = () => {
  const { t } = useTranslation();
  document.title = `${t('shared.calibration_details', 'Calibration Details')} | ArcMedix`;

  const user = JSON.parse(localStorage.getItem('authUser'));
  const organizationId = user?.activeOrganizationId ?? null;

  // State
  const [calibration, setCalibration] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [selectedEntry, setSelectedEntry] = useState(null);

  // Edit and Add data state
  const [editData, setEditData] = useState({
    title: '',
    previousCalibrationDate: '',
    dueDate: '',
    toolId: '',
    noCertificationRequired: false,
    comments: '',
    images: [''],
    documents: [''],
    calibrationStatus: 'SUBMITTED',
    usageStatus: '',
  });
  const [addData, setAddData] = useState({
    assetId: '',
    title: '',
    previousCalibrationDate: '',
    dueDate: '',
    toolId: '',
    noCertificationRequired: false,
    comments: '',
    images: [''],
    documents: [''],
    calibrationStatus: 'SUBMITTED',
    usageStatus: '',
  });


  // Asset selection state
  const [assets, setAssets] = useState([]);
  const [assetSearchTerm, setAssetSearchTerm] = useState('');
  const [showAssetDropdown, setShowAssetDropdown] = useState(false);
  const [selectedAssetDisplay, setSelectedAssetDisplay] = useState('');

  // Media viewer state
  const [imageViewerModal, setImageViewerModal] = useState(false);
  const [documentViewerModal, setDocumentViewerModal] = useState(false);
  const [currentMedia, setCurrentMedia] = useState([]);
  const [mediaType, setMediaType] = useState('');

  // File upload state
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const editFileInputRef = useRef(null);
  const addFileInputRef = useRef(null);

  // Export modal state
  const [fileModal, setFileModal] = useState(false);

  // Use new hooks
  const { tools } = useReferenceData(organizationId, {
    fetchLocations: false,
    fetchDepartments: false,
    fetchManufacturers: false,
    fetchTechnicians: false,
    fetchTools: true,
  });

  const {
    count,
    hasMore,
    setHasMore,
    handleLoadMore: onLoadMore,
  } = usePagination(10, 10);

  const { notification, showSuccess, showError } = useNotification(5000);

  const editModal = useModalState();
  const deleteModal = useModalState();
  const addModal = useModalState();
  const workOrderModal = useModalState();

  // Handlers
  const openMediaViewer = (media, type) => {
    setCurrentMedia(media || []);
    setMediaType(type);
    if (type === 'image') {
      setImageViewerModal(true);
    } else {
      setDocumentViewerModal(true);
    }
  };

  const getDocumentIcon = (url) => {
    const extension = url?.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'file-text';
      case 'doc':
      case 'docx':
        return 'file-text';
      case 'xls':
      case 'xlsx':
        return 'file-text';
      default:
        return 'file';
    }
  };

  const getFileNameFromUrl = (url) => {
    try {
      const urlParts = url.split('/');
      return decodeURIComponent(urlParts[urlParts.length - 1]);
    } catch {
      return t('shared.document', 'Document');
    }
  };

  const handleWorkOrderClick = (workOrder) => {
    workOrderModal.open({ ...workOrder, type: 'calibration' });
  };

  const fetchCalibrationData = async (isLoadMore = false) => {
    setLoading(true);
    try {
      const params = {
        count,
        ...(searchTerm && { searchTerm }),
      };

      const { data } = await api.get('calibration/get-all', { params });

      if (isLoadMore) {
        // Load more - append to existing data
        setCalibration((prev) => [...prev, ...(data.response || [])]);
      } else {
        // Initial load, search, or refresh - replace existing data
        setCalibration(data.response || []);
      }

      // Check if there are more results
      setHasMore(data.response && data.response.length === count);
    } catch (error) {
      console.error('Error fetching calibration data:', error);
      setCalibration([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setCalibration([]); // Clear current results
  };

  const handleEdit = (item) => {
    setSelectedEntry(item);
    const toolIdValue = item.tool?.id || item.toolId?.id || item.toolId || '';
    setEditData({
      title: item.title || '',
      previousCalibrationDate: item.previousCalibrationDate || '',
      dueDate: item.dueDate || '',
      toolId: toolIdValue,
      noCertificationRequired: item.noCertificationRequired || false,
      comments: item.comments || '',
      images: item.images || [''],
      documents: item.documents || [''],
      calibrationStatus: item.calibrationStatus || 'SUBMITTED',
      usageStatus: item.usageStatus || '',
    });
    editModal.open(item);
    setErrors({});
    setApiError('');
  };

  const handleDeleteClick = (item) => {
    deleteModal.open(item);
  };

  const handleAdd = () => {
    setAddData({
      assetId: '',
      title: '',
      previousCalibrationDate: '',
      toolId: '',
      noCertificationRequired: false,
      comments: '',
      images: [''],
      documents: [''],
      calibrationStatus: 'SUBMITTED',
      usageStatus: '',
    });
    setSelectedAssetDisplay('');
    setAssetSearchTerm('');
    setShowAssetDropdown(false);
    addModal.open();
    setErrors({});
    setApiError('');
  };

  const handleAssetSearchChange = (e) => {
    const value = e.target.value;
    setAssetSearchTerm(value);
    setShowAssetDropdown(value.length > 0);
    if (value.length === 0) {
      setAddData({
        ...addData,
        assetId: '',
        usageStatus: '',
      });
      setSelectedAssetDisplay('');
    }
  };

  // Helper function to convert DD-MM-YYYY to ISO format YYYY-MM-DDTHH:mm:ss.000
  const convertDateToISO = (dateStr) => {
    if (!dateStr) return '';

    // Check if already in ISO format
    if (dateStr.includes('T')) return dateStr;

    // Convert DD-MM-YYYY to YYYY-MM-DDTHH:mm:ss.000
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const [day, month, year] = parts;
      // Set time to 00:00:00.000 by default
      return `${year}-${month}-${day}T00:00:00.000`;
    }

    return dateStr;
  };

  const handleAssetSelect = (asset) => {
    const formattedDate = convertDateToISO(asset.previousCalibrationDate);

    setAddData({
      ...addData,
      assetId: asset.id,
      previousCalibrationDate: formattedDate,
      usageStatus: asset.assetStatus || '',
    });
    setSelectedAssetDisplay(
      `${asset.assetName} - ${asset.serialNumber} (${asset.currentLocation})`
    );
    setAssetSearchTerm('');
    setShowAssetDropdown(false);
  };

  const handleUpdate = async () => {
    try {
      setApiError('');

      // Validation
      const newErrors = {};
      if (!editData.usageStatus) {
        newErrors.usageStatus = t('calibration.detail.messages.usage_status_required', 'Usage status is required');
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      const payload = {
        id: selectedEntry.id,
        title: editData.title,
        previousCalibrationDate: editData.previousCalibrationDate,
        dueDate: editData.dueDate,
        toolId: editData.toolId || null,
        noCertificationRequired: editData.noCertificationRequired,
        comments: editData.comments,
        images: editData.images.filter((img) => img !== ''),
        documents: editData.documents.filter((doc) => doc !== ''),
        calibrationStatus: editData.calibrationStatus,
        usageStatus: editData.usageStatus,
        downtimeReasonId: editData.usageStatus === 'OFFLINE' ? (editData.downtimeReasonId || null) : null,
      };

      await api.put('calibration/update', payload);
      showSuccess(t('calibration.detail.messages.update_success', 'Calibration updated successfully'));
      editModal.close();
      setSelectedEntry(null);
      fetchCalibrationData(false); // Refresh data
    } catch (error) {
      console.error('Error updating calibration:', error);
      const errorMsg =
        error?.response?.data?.error || t('calibration.detail.messages.update_failed', 'Failed to update calibration');
      setApiError(errorMsg);
      showError(errorMsg);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`calibration/delete?id=${deleteModal.data?.id}`);
      showSuccess(t('calibration.detail.messages.delete_success', 'Calibration deleted successfully'));
      deleteModal.close();
      fetchCalibrationData(false); // Refresh data
    } catch (error) {
      console.error('Error deleting calibration:', error);
      const errorMsg =
        error?.response?.data?.error || t('calibration.detail.messages.delete_failed', 'Failed to delete calibration');
      showError(errorMsg);
    }
  };

  const handleCreate = async () => {
    try {
      setApiError('');

      // Validation
      const newErrors = {};
      if (addData.assetId && !addData.usageStatus) {
        newErrors.usageStatus = t('calibration.detail.messages.usage_status_required', 'Usage status is required');
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      const payload = {
        assetId: parseInt(addData.assetId),
        title: addData.title,
        previousCalibrationDate: addData.previousCalibrationDate,
        dueDate: addData.dueDate,
        toolId: addData.toolId || null,
        noCertificationRequired: addData.noCertificationRequired,
        comments: addData.comments,
        images: addData.images.filter((img) => img !== ''),
        documents: addData.documents.filter((doc) => doc !== ''),
        calibrationStatus: addData.calibrationStatus,
        usageStatus: addData.usageStatus,
        downtimeReasonId: addData.usageStatus === 'OFFLINE' ? (addData.downtimeReasonId || null) : null,
      };

      await api.post('calibration/create', payload);
      showSuccess(t('calibration.detail.messages.create_success', 'Calibration created successfully'));
      addModal.close();
      fetchCalibrationData(false); // Refresh data
    } catch (error) {
      console.error('Error creating calibration:', error);
      const errorMsg =
        error?.response?.data?.error || t('calibration.detail.messages.create_failed', 'Failed to create calibration');
      setApiError(errorMsg);
      showError(errorMsg);
    }
  };

  const fetchAssets = async () => {
    try {
      const params = {
        organizationId,
        count: 5, // Limit to top 5 results
        search: assetSearchTerm,
        hierarchy: false,
      };

      const { data } = await api.get(`asset/all`, { params });
      setAssets(data?.response[0]?.assets || []);
    } catch (error) {
      console.error('Error fetching assets:', error);
      setAssets([]);
    }
  };

  const handleFileUpload = async (e, isAdd = false) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Get the ref for resetting later
    const inputRef = isAdd ? addFileInputRef : editFileInputRef;

    // Validate files before upload
    const validation = validateFiles(files);

    if (validation.errors.length > 0) {
      setApiError(validation.errors.join('\n'));
    }

    // Only proceed if there are valid files
    if (validation.validFiles.length === 0) {
      if (inputRef.current) inputRef.current.value = '';
      return;
    }

    setUploadingFiles(true);
    try {
      const uploadedUrls = await uploadServiceContractFiles(
        validation.validFiles
      );

      if (uploadedUrls && uploadedUrls.length > 0) {
        // Auto-categorize uploaded files by extension
        const newImageUrls = [];
        const newDocumentUrls = [];

        uploadedUrls.forEach((url) => {
          if (/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(url)) {
            newImageUrls.push(url);
          } else {
            newDocumentUrls.push(url);
          }
        });

        if (isAdd) {
          setAddData((prev) => ({
            ...prev,
            images: [
              ...prev.images.filter((img) => img !== ''),
              ...newImageUrls,
            ],
            documents: [
              ...prev.documents.filter((doc) => doc !== ''),
              ...newDocumentUrls,
            ],
          }));
        } else {
          setEditData((prev) => ({
            ...prev,
            images: [
              ...prev.images.filter((img) => img !== ''),
              ...newImageUrls,
            ],
            documents: [
              ...prev.documents.filter((doc) => doc !== ''),
              ...newDocumentUrls,
            ],
          }));
        }
      }
    } catch (err) {
      console.error('Upload error:', err);
      setApiError(t('calibration.detail.messages.upload_error', 'Error during upload: ') + (err.message || 'Unknown error'));
    } finally {
      setUploadingFiles(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDeleteImage = (index, isAdd = false) => {
    if (isAdd) {
      const newImages = addData.images.filter((_, i) => i !== index);
      setAddData({ ...addData, images: newImages.length ? newImages : [''] });
    } else {
      const newImages = editData.images.filter((_, i) => i !== index);
      setEditData({ ...editData, images: newImages.length ? newImages : [''] });
    }
  };

  const handleDeleteDocument = (index, isAdd = false) => {
    if (isAdd) {
      const newDocuments = addData.documents.filter((_, i) => i !== index);
      setAddData({
        ...addData,
        documents: newDocuments.length ? newDocuments : [''],
      });
    } else {
      const newDocuments = editData.documents.filter((_, i) => i !== index);
      setEditData({
        ...editData,
        documents: newDocuments.length ? newDocuments : [''],
      });
    }
  };

  const handleImport = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleExport = () => {
    if (!calibration || calibration.length === 0) return;

    const dataToExport = calibration.map((item, index) => ({
      // Basic Info
      '#': index + 1,
      'Request ID': item?.requestId || '',
      Title: item?.title || '',

      // Status
      Status: item?.calibrationStatus || '',
      'Usage Status': item?.usageStatus || '',
      'Certification Required': item?.noCertificationRequired ? 'No' : 'Yes',

      // Dates
      'Created Date': formatCreatedDate(item?.createdAt),
      'Due Date': formatCreatedDate(item?.dueDate),
      'Previous Calibration Date': formatCreatedDate(
        item?.previousCalibrationDate
      ),

      // Asset Info
      'Asset ID': item?.asset?.id || '',
      'Asset Name': item?.asset?.assetName || '',
      'Asset Types': item?.asset?.assetTypes?.map(at => at.name).join(', ') || '',
      'Serial Number': item?.asset?.serialNumber || '',
      Manufacturer: item?.asset?.manufacturer || '',
      Model: item?.asset?.model || '',
      Location: item?.asset?.currentLocation || '',

      // Tool Info
      'Tool Name': item?.tool?.name || '',

      // Comments
      Comments: item?.comments || '',
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Calibration Work Orders');

    XLSX.writeFile(
      wb,
      `calibration_work_orders_${new Date().toISOString().split('T')[0]}.xlsx`
    );
  };

  useEffect(() => {
    if (count > 5) {
      // This is a load more action
      fetchCalibrationData(true);
    }
  }, [count]);

  useEffect(() => {
    // Initial load on component mount
    fetchCalibrationData(false);
  }, []);

  useEffect(() => {
    if (organizationId && assetSearchTerm.length > 0) {
      fetchAssets();
    } else if (organizationId && assetSearchTerm.length === 0) {
      setAssets([]); // Clear assets when search is empty
    }
  }, [organizationId, assetSearchTerm]);

  // Debounced search effect
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      fetchCalibrationData(false); // Fresh search, not load more
    }, 300); // 300ms delay

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  const renderMediaPreview = (media, type) => {
    if (!media || media.length === 0 || media[0] === '') {
      return (
        <Button className="btn btn-soft-success waves-effect waves-light">
          {t('shared.view', 'View')}
        </Button>
      );
    }
    return (
      <Button className="btn btn-soft-success waves-effect waves-light">
        {t('shared.view', 'View')} ({media.length})
      </Button>
    );
  };

  return (
    <div className="page-content mt-4">
      <NotificationAlert
        type={notification.type}
        message={notification.message}
      />

      <div className="mb-4 d-flex align-items-center gap-3">
        <motion.div
          className="mb-4 d-flex align-items-center gap-3"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <button
            type="button"
            className="btn btn-soft-success waves-effect waves-light d-flex align-items-center gap-2 transition-all hover-shake"
            onClick={() => handleAdd()}
          >
            <FeatherIcon icon="plus" />
            {t('calibration.detail.add_calibration', 'Add Calibration')}
          </button>
          <div
            className="ms-auto"
            style={{ minWidth: '400px', flexGrow: 1, maxWidth: '500px' }}
          >
            <Input
              type="text"
              placeholder={t('calibration.detail.search_placeholder', 'Search calibration details...')}
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
        </motion.div>
      </div>

      <Card>
        <CardHeader className="d-flex justify-content-between align-items-center">
          <h3 className="card-title text-success">{t('shared.calibration_details', 'Calibration Details')}</h3>
          <button
            className="btn btn-soft-info p-2"
            onClick={() => setFileModal(true)}
            title={t('calibration.detail.modals.export_title', 'Export Calibration')}
          >
            <FeatherIcon icon="file-text" size={18} />
          </button>
        </CardHeader>

        <CardBody>
          <div
            className="table-responsive"
            style={{
              overflowX: 'auto',
              whiteSpace: 'nowrap',
              maxHeight: 'calc(100vh - 300px)',
              overflowY: 'auto',
            }}
          >
            <table
              className="table table-hover mb-0"
              style={{
                borderSpacing: '0.1rem',
                borderCollapse: 'separate',
              }}
            >
              <thead
                style={{
                  position: 'sticky',
                  top: 0,
                  background: 'white',
                  zIndex: 1,
                }}
              >
                <tr>
                  <th>{t('shared.id', '#')}</th>
                  <th>{t('shared.ticket_number', 'Ticket Number')}</th>
                  <th>{t('shared.asset', 'Asset')}</th>
                  <th>{t('shared.tool', 'Tool')}</th>
                  <th>{t('shared.previous_date', 'Previous Date')}</th>
                  <th>{t('shared.due_date', 'Due Date')}</th>
                  <th>{t('shared.status', 'Status')}</th>
                  <th>{t('shared.certification_required', 'Certification Required')}</th>
                  <th>{t('shared.delete', 'Delete')}</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {calibration?.map((item, ind) => (
                    <motion.tr
                      key={item?.id || ind}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <th scope="row">{ind + 1}</th>
                      <td>
                        {item.requestId ? (
                          <span
                            className="text-primary fw-medium"
                            role="button"
                            onClick={() => handleWorkOrderClick(item)}
                            style={{ cursor: 'pointer' }}
                            title={t('shared.click_view_details', 'Click to view details')}
                          >
                            {item.requestId}
                          </span>
                        ) : (
                          <span className="text-muted">{t('shared.n_a', 'N/A')}</span>
                        )}
                      </td>
                      <td>{item.asset?.assetName || t('shared.n_a', 'N/A')}</td>
                      <td>{item.tool?.name || t('shared.n_a', 'N/A')}</td>
                      <td>
                        {formatCreatedDate(item?.previousCalibrationDate) ||
                          t('shared.n_a', 'N/A')}
                      </td>
                      <td>{formatCreatedDate(item?.dueDate) || t('shared.n_a', 'N/A')}</td>
                      <td>
                        {item.calibrationStatus ? (
                          (() => {
                            const option = workstatusOptions(t).find(
                              (opt) => opt.value === item.calibrationStatus
                            ) || {
                              label: item.calibrationStatus,
                              icon: 'help-circle',
                              colorClass: 'bg-secondary text-white',
                              borderColor: '#6c757d',
                            };
                            return (
                              <span
                                className={`badge rounded-pill py-1 px-2 d-inline-flex align-items-center gap-1 ${option.colorClass}`}
                                style={{
                                  border: `1px solid ${option.borderColor}`,
                                }}
                              >
                                <FeatherIcon icon={option.icon} size={12} />
                                {option.label}
                              </span>
                            );
                          })()
                        ) : (
                          <span className="text-muted">{t('shared.n_a', 'N/A')}</span>
                        )}
                      </td>
                      <td>
                        <Badge
                          color={
                            !item?.noCertificationRequired
                              ? 'success'
                              : 'warning'
                          }
                        >
                          {!item?.noCertificationRequired
                            ? t('calibration.detail.status.required', 'Required')
                            : t('calibration.detail.status.not_required', 'Not Required')}
                        </Badge>
                      </td>
                      <td
                        className="text-danger"
                        role="button"
                        onClick={() => handleDeleteClick(item)}
                      >
                        <FeatherIcon icon="trash-2" />
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          <LoadMoreButton
            loading={loading}
            hasMore={hasMore}
            onLoadMore={onLoadMore}
            itemCount={calibration.length}
            emptyMessage={
              searchTerm
                ? t('calibration.detail.messages.no_matching_results', `No calibration details found matching "{{searchTerm}}"`, { searchTerm })
                : t('calibration.detail.messages.no_results_found', 'No calibration details found')
            }
          />
        </CardBody>
      </Card>

      {/* Image Viewer Modal */}
      <Modal
        isOpen={imageViewerModal}
        toggle={() => setImageViewerModal(false)}
        size="lg"
      >
        <div className="modal-header">
          <h5 className="modal-title">{t('shared.asset_images', 'Asset Images')}</h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setImageViewerModal(false)}
            aria-label={t("shared.close", "Close")}
          ></button>
        </div>
        <div className="modal-body">
          {currentMedia.length > 0 && currentMedia[0] !== '' ? (
            <Row>
              {currentMedia.map((mediaUrl, index) => (
                <Col md={6} className="mb-4" key={index}>
                  <div className="card border-0 shadow-sm">
                    <div className="position-relative">
                      <img
                        src={mediaUrl}
                        alt={t('shared.asset_image', 'Asset Image') + ` ${index + 1}`}
                        className="img-fluid rounded-top"
                        style={{
                          objectFit: 'cover',
                          height: '200px',
                          width: '100%',
                        }}
                      />
                    </div>
                    <div className="card-body p-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <small className="text-muted">{t('shared.asset_image', 'Asset Image')} {index + 1}</small>
                        <a
                          href={mediaUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-primary"
                        >
                          {t('calibration.detail.modals.view_full_image', 'View Full')}
                        </a>
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          ) : (
            <div className="text-center py-4">
              <FeatherIcon icon="image" size="48" className="text-muted mb-2" />
              <p className="text-muted">{t('calibration.detail.messages.no_images', 'No images available')}</p>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setImageViewerModal(false)}
          >
            {t('shared.close', 'Close')}
          </button>
        </div>
      </Modal>

      {/* Document Viewer Modal */}
      <Modal
        isOpen={documentViewerModal}
        toggle={() => setDocumentViewerModal(false)}
        size="lg"
      >
        <div className="modal-header">
          <h5 className="modal-title">{t('calibration.detail.modals.document_viewer_title', 'Calibration Documents')}</h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setDocumentViewerModal(false)}
            aria-label={t("shared.close", "Close")}
          ></button>
        </div>
        <div className="modal-body">
          {currentMedia.length > 0 && currentMedia[0] !== '' ? (
            <Row>
              {currentMedia.map((docUrl, index) => {
                const isPdf = docUrl?.toLowerCase().endsWith('.pdf');
                const isImage = ['jpg', 'jpeg', 'png', 'gif'].some((ext) =>
                  docUrl?.toLowerCase().endsWith(ext)
                );

                return (
                  <Col md={6} className="mb-4" key={index}>
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body text-center">
                        <FeatherIcon
                          icon={
                            isPdf ? 'file-text' : isImage ? 'image' : 'file'
                          }
                          size="48"
                          className="text-primary mb-3"
                        />
                        <h6 className="mb-2">{t('shared.document', 'Document')} {index + 1}</h6>
                        <small className="text-muted d-block mb-3 text-truncate">
                          {docUrl?.split('/').pop()}
                        </small>
                        <a
                          href={docUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-primary"
                        >
                          {isPdf
                            ? t('calibration.detail.modals.view_pdf', 'View PDF')
                            : isImage
                            ? t('calibration.detail.modals.view_image', 'View Image')
                            : t('calibration.detail.modals.download', 'Download')}
                        </a>
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          ) : (
            <div className="text-center py-4">
              <FeatherIcon icon="file" size="48" className="text-muted mb-2" />
              <p className="text-muted">{t('calibration.detail.messages.no_documents', 'No documents available')}</p>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setDocumentViewerModal(false)}
          >
            {t('shared.close', 'Close')}
          </button>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={editModal.isOpen} toggle={editModal.close} size="lg">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.2 }}
        >
          <div className="modal-header text-white">
            <h5 className="modal-title fs-4">{t('calibration.detail.modals.edit_title', 'Edit Calibration Details')}</h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={editModal.close}
              aria-label={t("shared.close", "Close")}
            ></button>
          </div>

          <div className="modal-body p-4">
            <form>
              <Row className="mb-3">
                <Col md={12}>
                  <div className="form-group">
                    <label htmlFor="title" className="form-label fw-medium">
                      {t('calibration.detail.modals.label_title', 'Title')}
                    </label>
                    <input
                      type="text"
                      id="title"
                      className="form-control"
                      value={editData.title}
                      onChange={(e) =>
                        setEditData({ ...editData, title: e.target.value })
                      }
                      placeholder={t('calibration.detail.modals.enter_title_placeholder', 'Enter calibration title...')}
                    />
                  </div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={12}>
                  <div className="form-group">
                    <label htmlFor="toolId" className="form-label fw-medium">
                      {t('calibration.detail.modals.label_tool', 'Tool')}
                    </label>
                    <select
                      id="toolId"
                      className="form-select"
                      value={editData.toolId || ''}
                      onChange={(e) =>
                        setEditData({ ...editData, toolId: e.target.value })
                      }
                    >
                      <option value="">{t('calibration.detail.modals.select_tool_option', 'Select Tool')}</option>
                      {tools.map((tool) => (
                        <option key={tool.id} value={tool.id}>
                          {tool.name} - {tool.serialNumber}
                        </option>
                      ))}
                    </select>
                  </div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <div className="form-group">
                    <label htmlFor="dueDate" className="form-label fw-medium">
                      {t('calibration.detail.modals.label_due_date', 'Task Due Date *')}
                    </label>
                    <DatePicker
                      id="dueDate"
                      selected={
                        editData?.dueDate ? new Date(editData.dueDate) : null
                      }
                      onChange={(date) => {
                        if (date) {
                          const formattedDate = formatDateTimeForAPI(date);
                          setEditData({
                            ...editData,
                            dueDate: formattedDate,
                          });
                          if (errors.dueDate) {
                            setErrors((prev) => ({ ...prev, dueDate: '' }));
                          }
                        }
                      }}
                      className={`form-control ${
                        errors.dueDate ? 'is-invalid' : ''
                      }`}
                      dateFormat="MMMM d, yyyy"
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={15}
                      timeCaption="time"
                      isClearable
                    />
                    {errors.dueDate && (
                      <div className="text-danger small mt-1">
                        {errors.dueDate}
                      </div>
                    )}
                  </div>
                </Col>
                <Col md={6}>
                  <div className="form-group">
                    <label
                      htmlFor="previousCalibrationDate"
                      className="form-label fw-medium"
                    >
                      {t('calibration.detail.modals.label_previous_date', 'Previous Calibration Date *')}
                    </label>
                    <input
                      type="datetime-local"
                      id="previousCalibrationDate"
                      className={`form-control ${
                        errors.previousCalibrationDate ? 'is-invalid' : ''
                      }`}
                      value={
                        editData.previousCalibrationDate
                          ? formatDateTimeForAPI(
                              editData.previousCalibrationDate
                            )
                          : ''
                      }
                      onChange={(e) =>
                        setEditData({
                          ...editData,
                          previousCalibrationDate: e.target.value,
                        })
                      }
                    />
                    {errors.previousCalibrationDate && (
                      <small className="text-danger">
                        {errors.previousCalibrationDate}
                      </small>
                    )}
                  </div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <div className="form-group">
                    <label
                      htmlFor="calibrationStatus"
                      className="form-label fw-medium"
                    >
                      {t('calibration.detail.modals.label_status', 'Status')}
                    </label>
                    <select
                      id="calibrationStatus"
                      className="form-select"
                      value={editData.calibrationStatus || 'SUBMITTED'}
                      onChange={(e) =>
                        setEditData({
                          ...editData,
                          calibrationStatus: e.target.value,
                        })
                      }
                    >
                      {workstatusOptions(t).map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </Col>
                <AssetUsageStatusFields
                  usageStatus={editData.usageStatus}
                  downtimeReasonId={editData.downtimeReasonId}
                  onChange={({ usageStatus, downtimeReasonId }) => setEditData({ ...editData, usageStatus, downtimeReasonId })}
                  errors={errors}
                  layout="inline"
                  required
                  id="edit"
                />
              </Row>

              <Row className="mb-3">
                <Col md={12}>
                  <div className="form-group">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="noCertificationRequired"
                        checked={editData.noCertificationRequired}
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            noCertificationRequired: e.target.checked,
                          })
                        }
                      />
                      <label
                        className="form-check-label"
                        htmlFor="noCertificationRequired"
                      >
                        {t('calibration.detail.modals.label_no_cert_required', 'No Certification Required')}
                      </label>
                    </div>
                  </div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={12}>
                  <div className="form-group">
                    <label htmlFor="comments" className="form-label fw-medium">
                      {t('calibration.detail.modals.label_comments', 'Comments')}
                    </label>
                    <textarea
                      id="comments"
                      rows="3"
                      className="form-control"
                      value={editData.comments}
                      onChange={(e) =>
                        setEditData({ ...editData, comments: e.target.value })
                      }
                      placeholder={t('calibration.detail.modals.enter_comments_placeholder', 'Enter any comments or notes...')}
                    />
                  </div>
                </Col>
              </Row>

              {/* Media Section */}
              <Row className="mb-3">
                <Col md={12}>
                  <div className="form-group">
                    {/* Header with upload button */}
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="d-flex align-items-center">
                        <div
                          className="bg-info bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3"
                          style={{ width: '40px', height: '40px' }}
                        >
                          <FeatherIcon
                            icon="paperclip"
                            size={20}
                            className="text-info"
                          />
                        </div>
                        <div>
                          <h6 className="mb-0 fw-bold">{t('calibration.detail.modals.media_section_title', 'Media')}</h6>
                          <small className="text-muted">
                            {t('calibration.detail.modals.media_section_subtitle', 'Service reports, images, and documents')}
                          </small>
                        </div>
                      </div>
                      <Button
                        color="primary"
                        onClick={() => editFileInputRef.current?.click()}
                        disabled={uploadingFiles}
                        className="d-flex align-items-center gap-2"
                      >
                        {uploadingFiles ? (
                          <>
                            <Spinner size="sm" />
                            {t('calibration.detail.modals.uploading_status', 'Uploading...')}
                          </>
                        ) : (
                          <>
                            <FeatherIcon icon="upload" size={16} />
                            Upload Files
                          </>
                        )}
                      </Button>
                      <input
                        type="file"
                        ref={editFileInputRef}
                        multiple
                        hidden
                        onChange={(e) => handleFileUpload(e, false)}
                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                      />
                    </div>

                    {/* Images Section */}
                    {editData.images.filter((img) => img !== '').length > 0 && (
                      <div className="mb-4">
                        <h6 className="fw-semibold mb-3">
                          <FeatherIcon
                            icon="image"
                            size={16}
                            className="me-2"
                          />
                          {t('shared.images', 'Images')} ({editData.images.filter((img) => img !== '').length})
                        </h6>
                        <Row className="g-3">
                          {editData.images
                            .filter((img) => img !== '')
                            .map((url, index) => (
                              <Col key={index} md={3} sm={4} xs={6}>
                                <motion.div
                                  className="position-relative"
                                  whileHover={{ scale: 1.02 }}
                                  transition={{ duration: 0.2 }}
                                >
                                  <img
                                    src={url}
                                    alt={t('calibration.detail.modals.attachment_alt', 'Attachment') + ` ${index + 1}`}
                                    className="img-fluid rounded shadow-sm"
                                    style={{
                                      width: '100%',
                                      height: '120px',
                                      objectFit: 'cover',
                                      cursor: 'pointer',
                                    }}
                                    onClick={() => window.open(url, '_blank')}
                                  />
                                  <div className="position-absolute top-0 end-0 p-1">
                                    <Button
                                      color="danger"
                                      size="sm"
                                      className="rounded-circle p-1"
                                      style={{ width: '28px', height: '28px' }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteImage(index, false);
                                      }}
                                    >
                                      <FeatherIcon icon="trash-2" size={12} />
                                    </Button>
                                  </div>
                                  <div className="position-absolute bottom-0 start-0 end-0 p-1">
                                    <Button
                                      color="light"
                                      size="sm"
                                      className="w-100 opacity-75"
                                      onClick={() => window.open(url, '_blank')}
                                    >
                                      <FeatherIcon
                                        icon="download"
                                        size={12}
                                        className="me-1"
                                      />
                                      {t('calibration.detail.modals.download', 'Download')}
                                    </Button>
                                  </div>
                                </motion.div>
                              </Col>
                            ))}
                        </Row>
                      </div>
                    )}

                    {/* Documents Section */}
                    {editData.documents.filter((doc) => doc !== '').length >
                      0 && (
                      <div className="mb-4">
                        <h6 className="fw-semibold mb-3">
                          <FeatherIcon
                            icon="file-text"
                            size={16}
                            className="me-2"
                          />
                          {t('shared.documents', 'Documents')} ({editData.documents.filter((doc) => doc !== '').length})
                        </h6>
                        <div className="documents-list">
                          {editData.documents
                            .filter((doc) => doc !== '')
                            .map((url, index) => (
                              <motion.div
                                key={index}
                                className="d-flex align-items-center p-3 bg-light rounded mb-2"
                                whileHover={{ backgroundColor: '#e9ecef' }}
                                transition={{ duration: 0.2 }}
                              >
                                <div
                                  className="bg-white rounded d-flex align-items-center justify-content-center me-3"
                                  style={{ width: '40px', height: '40px' }}
                                >
                                  <FeatherIcon
                                    icon={getDocumentIcon(url)}
                                    size={20}
                                    className="text-primary"
                                  />
                                </div>
                                <div className="flex-grow-1 overflow-hidden">
                                  <p className="mb-0 fw-medium text-truncate">
                                    {getFileNameFromUrl(url)}
                                  </p>
                                  <small className="text-muted">
                                    {url.split('.').pop()?.toUpperCase()}{' '}
                                    {t('shared.document', 'Document')}
                                  </small>
                                </div>
                                <div className="d-flex gap-2">
                                  <Button
                                    color="outline-primary"
                                    size="sm"
                                    onClick={() => window.open(url, '_blank')}
                                  >
                                    <FeatherIcon icon="download" size={14} />
                                  </Button>
                                  <Button
                                    color="outline-danger"
                                    size="sm"
                                    onClick={() =>
                                      handleDeleteDocument(index, false)
                                    }
                                  >
                                    <FeatherIcon icon="trash-2" size={14} />
                                  </Button>
                                </div>
                              </motion.div>
                            ))}
                        </div>
                      </div>
                    )}

                    {/* Empty State */}
                    {!editData.images.filter((img) => img !== '').length &&
                      !editData.documents.filter((doc) => doc !== '')
                        .length && (
                        <div className="text-center py-4 bg-light rounded">
                          <div
                            className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                            style={{
                              width: '64px',
                              height: '64px',
                              backgroundColor: '#f8f9fa',
                            }}
                          >
                            <FeatherIcon
                              icon="paperclip"
                              size={24}
                              className="text-muted"
                            />
                          </div>
                          <h6 className="text-muted">{t('shared.no_media', 'No media files')}</h6>
                          <p className="text-muted mb-0">
                            {t('shared.upload_instructions', 'Upload images or documents using the button above')}
                          </p>
                        </div>
                      )}
                  </div>
                </Col>
              </Row>

              {apiError && (
                <div className="alert alert-danger mb-3">{apiError}</div>
              )}
            </form>
          </div>

          <div className="modal-footer bg-light border-top-0">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={editModal.close}
            >
              {t('shared.cancel', 'Cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleUpdate}
            >
              {t('calibration.detail.modals.button_update', 'Update Calibration')}
            </button>
          </div>
        </motion.div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={deleteModal.isOpen}
        toggle={deleteModal.close}
        onConfirm={handleDelete}
        title={t('shared.confirm_delete', 'Confirm Delete')}
        message={
          <>
            <h6>{t('calibration.detail.messages.confirm_delete_message', 'Are you sure you want to delete this calibration record?')}</h6>
            <p className="text-muted">
              {t('calibration.detail.modals.label_asset', 'Asset:')} <strong>{deleteModal.data?.asset?.assetName}</strong>
              <br />
              {t('shared.due_date', 'Due Date')}:{' '}
              <strong>{formatCreatedDate(deleteModal.data?.dueDate)}</strong>
            </p>
          </>
        }
      />

      {/* Add Modal */}
      <Modal isOpen={addModal.isOpen} toggle={addModal.close} size="lg">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.2 }}
        >
          <div className="modal-header text-white">
            <h5 className="modal-title fs-4">{t('calibration.detail.modals.add_title', 'Add New Calibration')}</h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={addModal.close}
              aria-label={t("shared.close", "Close")}
            ></button>
          </div>

          <div className="modal-body p-4">
            <form>
              <Row className="mb-3">
                <Col md={12}>
                  <div className="form-group">
                    <label
                      htmlFor="assetSearch"
                      className="form-label fw-medium"
                    >
                      {t('calibration.detail.modals.label_select_asset', 'Select Asset *')}
                    </label>
                    <div className="position-relative">
                      {selectedAssetDisplay ? (
                        <div className="d-flex">
                          <div
                            className="form-control d-flex align-items-center justify-content-between"
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              setSelectedAssetDisplay('');
                              setAddData({
                                ...addData,
                                assetId: '',
                                usageStatus: '',
                              });
                            }}
                          >
                            <span>{selectedAssetDisplay}</span>
                            <FeatherIcon
                              icon="x"
                              size={16}
                              className="text-muted"
                            />
                          </div>
                        </div>
                      ) : (
                        <>
                          <Input
                            type="text"
                            id="assetSearch"
                            placeholder={t('calibration.detail.modals.search_assets_placeholder', 'Type to search assets...')}
                            value={assetSearchTerm}
                            onChange={handleAssetSearchChange}
                            onFocus={() => {
                              if (assetSearchTerm.length > 0) {
                                setShowAssetDropdown(true);
                              }
                            }}
                            onBlur={() => {
                              // Delay hiding dropdown to allow clicks on items
                              setTimeout(
                                () => setShowAssetDropdown(false),
                                200
                              );
                            }}
                            className={errors.assetId ? 'is-invalid' : ''}
                          />
                          {showAssetDropdown && assets.length > 0 && (
                            <div
                              className="position-absolute w-100 bg-white border border-top-0 rounded-bottom shadow-sm"
                              style={{
                                zIndex: 1050,
                                maxHeight: '200px',
                                overflowY: 'auto',
                                top: '100%',
                              }}
                            >
                              {assets.map((asset) => (
                                <div
                                  key={asset.id}
                                  className="p-2 border-bottom cursor-pointer hover-bg-light d-flex align-items-center"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => handleAssetSelect(asset)}
                                  onMouseEnter={(e) =>
                                    (e.target.style.backgroundColor = '#f8f9fa')
                                  }
                                  onMouseLeave={(e) =>
                                    (e.target.style.backgroundColor = 'white')
                                  }
                                >
                                  <div className="flex-grow-1">
                                    <div className="fw-medium text-dark">
                                      {asset.assetName}
                                    </div>
                                    <small className="text-muted">
                                      {asset.serialNumber} •{' '}
                                      {asset.currentLocation}
                                    </small>
                                  </div>
                                  <FeatherIcon
                                    icon="chevron-right"
                                    size={16}
                                    className="text-muted"
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                          {showAssetDropdown &&
                            assetSearchTerm.length > 0 &&
                            assets.length === 0 && (
                              <div
                                className="position-absolute w-100 bg-white border border-top-0 rounded-bottom shadow-sm p-3 text-center text-muted"
                                style={{
                                  zIndex: 1050,
                                  top: '100%',
                                }}
                              >
                                {t('shared.no_assets_found_matching', `No assets found matching "{{searchTerm}}"`, { searchTerm: assetSearchTerm })}
                              </div>
                            )}
                        </>
                      )}
                    </div>
                    {errors.assetId && (
                      <small className="text-danger">{errors.assetId}</small>
                    )}
                  </div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={12}>
                  <div className="form-group">
                    <label htmlFor="addTitle" className="form-label fw-medium">
                      {t('calibration.detail.modals.label_title', 'Title')}
                    </label>
                    <input
                      type="text"
                      id="addTitle"
                      className="form-control"
                      value={addData.title}
                      onChange={(e) =>
                        setAddData({ ...addData, title: e.target.value })
                      }
                      placeholder={t('calibration.detail.modals.enter_title_placeholder', 'Enter calibration title...')}
                    />
                  </div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={12}>
                  <div className="form-group">
                    <label htmlFor="addToolId" className="form-label fw-medium">
                      {t('calibration.detail.modals.label_tool', 'Tool')}
                    </label>
                    <select
                      id="addToolId"
                      className="form-select"
                      value={addData.toolId || ''}
                      onChange={(e) =>
                        setAddData({ ...addData, toolId: e.target.value })
                      }
                    >
                      <option value="">{t('calibration.detail.modals.select_tool_option', 'Select Tool')}</option>
                      {tools.map((tool) => (
                        <option key={tool.id} value={tool.id}>
                          {tool.name} - {tool.serialNumber}
                        </option>
                      ))}
                    </select>
                  </div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <div className="form-group">
                    <label
                      htmlFor="addDueDate"
                      className="form-label fw-medium"
                    >
                      {t('calibration.detail.modals.label_due_date', 'Task Due Date *')}
                    </label>
                    <DatePicker
                      id="addDueDate"
                      selected={
                        addData?.dueDate ? new Date(addData.dueDate) : null
                      }
                      onChange={(date) => {
                        if (date) {
                          const formattedDate = formatDateTimeForAPI(date);
                          setAddData({
                            ...addData,
                            dueDate: formattedDate,
                          });
                          if (errors.dueDate) {
                            setErrors((prev) => ({ ...prev, dueDate: '' }));
                          }
                        }
                      }}
                      className={`form-control ${
                        errors.dueDate ? 'is-invalid' : ''
                      }`}
                      dateFormat="MMMM d, yyyy"
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={15}
                      timeCaption="time"
                      isClearable
                    />
                    {errors.dueDate && (
                      <div className="text-danger small mt-1">
                        {errors.dueDate}
                      </div>
                    )}
                  </div>
                </Col>
                <Col md={6}>
                  <div className="form-group">
                    <label
                      htmlFor="addPreviousCalibrationDate"
                      className="form-label fw-medium"
                    >
                      {t('calibration.detail.modals.label_previous_date', 'Previous Calibration Date *')}
                    </label>
                    <input
                      type="text"
                      id="addPreviousCalibrationDate"
                      className={`form-control bg-light ${
                        errors.previousCalibrationDate ? 'is-invalid' : ''
                      }`}
                      value={
                        addData.previousCalibrationDate
                          ? formatCreatedDate(addData.previousCalibrationDate)
                          : t('calibration.detail.messages.no_previous_date', 'No previous calibration date available')
                      }
                      readOnly
                      disabled
                    />
                    <small className="text-muted">
                      <FeatherIcon icon="info" size={12} className="me-1" />
                      {t('calibration.detail.messages.auto_populated_info', 'Automatically populated from selected asset')}
                    </small>
                    {errors.previousCalibrationDate && (
                      <small className="text-danger d-block">
                        {errors.previousCalibrationDate}
                      </small>
                    )}
                  </div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <div className="form-group">
                    <label
                      htmlFor="addCalibrationStatus"
                      className="form-label fw-medium"
                    >
                      {t('calibration.detail.modals.label_status', 'Status')}
                    </label>
                    <select
                      id="addCalibrationStatus"
                      className="form-select"
                      value={addData.calibrationStatus || 'SUBMITTED'}
                      onChange={(e) =>
                        setAddData({
                          ...addData,
                          calibrationStatus: e.target.value,
                        })
                      }
                    >
                      {workstatusOptions(t).map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </Col>
                {addData.assetId && (
                <AssetUsageStatusFields
                  usageStatus={addData.usageStatus}
                  downtimeReasonId={addData.downtimeReasonId}
                  onChange={({ usageStatus, downtimeReasonId }) => setAddData({ ...addData, usageStatus, downtimeReasonId })}
                  errors={errors}
                  layout="inline"
                  required
                  id="add"
                />
                )}
              </Row>

              <Row className="mb-3">
                <Col md={12}>
                  <div className="form-group">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="addNoCertificationRequired"
                        checked={addData.noCertificationRequired}
                        onChange={(e) =>
                          setAddData({
                            ...addData,
                            noCertificationRequired: e.target.checked,
                          })
                        }
                      />
                      <label
                        className="form-check-label"
                        htmlFor="addNoCertificationRequired"
                      >
                        {t('calibration.detail.modals.label_no_cert_required', 'No Certification Required')}
                      </label>
                    </div>
                  </div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={12}>
                  <div className="form-group">
                    <label
                      htmlFor="addComments"
                      className="form-label fw-medium"
                    >
                      {t('calibration.detail.modals.label_comments', 'Comments')}
                    </label>
                    <textarea
                      id="addComments"
                      rows="3"
                      className="form-control"
                      value={addData.comments}
                      onChange={(e) =>
                        setAddData({ ...addData, comments: e.target.value })
                      }
                      placeholder={t('calibration.detail.modals.enter_comments_placeholder', 'Enter any comments or notes...')}
                    />
                  </div>
                </Col>
              </Row>

              {/* Media Section */}
              <Row className="mb-3">
                <Col md={12}>
                  <div className="form-group">
                    {/* Header with upload button */}
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="d-flex align-items-center">
                        <div
                          className="bg-info bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3"
                          style={{ width: '40px', height: '40px' }}
                        >
                          <FeatherIcon
                            icon="paperclip"
                            size={20}
                            className="text-info"
                          />
                        </div>
                        <div>
                          <h6 className="mb-0 fw-bold">{t('calibration.detail.modals.media_section_title', 'Media')}</h6>
                          <small className="text-muted">
                            {t('calibration.detail.modals.media_section_subtitle', 'Service reports, images, and documents')}
                          </small>
                        </div>
                      </div>
                      <Button
                        color="primary"
                        onClick={() => addFileInputRef.current?.click()}
                        disabled={uploadingFiles}
                        className="d-flex align-items-center gap-2"
                      >
                        {uploadingFiles ? (
                          <>
                            <Spinner size="sm" />
                            {t('calibration.detail.modals.uploading_status', 'Uploading...')}
                          </>
                        ) : (
                          <>
                            <FeatherIcon icon="upload" size={16} />
                            {t('shared.upload_files', 'Upload Files')}
                          </>
                        )}
                      </Button>
                      <input
                        type="file"
                        ref={addFileInputRef}
                        multiple
                        hidden
                        onChange={(e) => handleFileUpload(e, true)}
                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                      />
                    </div>

                    {/* Images Section */}
                    {addData.images.filter((img) => img !== '').length > 0 && (
                      <div className="mb-4">
                        <h6 className="fw-semibold mb-3">
                          <FeatherIcon
                            icon="image"
                            size={16}
                            className="me-2"
                          />
                          {t('shared.images', 'Images')} ({addData.images.filter((img) => img !== '').length})
                        </h6>
                        <Row className="g-3">
                          {addData.images
                            .filter((img) => img !== '')
                            .map((url, index) => (
                              <Col key={index} md={3} sm={4} xs={6}>
                                <motion.div
                                  className="position-relative"
                                  whileHover={{ scale: 1.02 }}
                                  transition={{ duration: 0.2 }}
                                >
                                  <img
                                    src={url}
                                    alt={t('calibration.detail.modals.attachment_alt', 'Attachment') + ` ${index + 1}`}
                                    className="img-fluid rounded shadow-sm"
                                    style={{
                                      width: '100%',
                                      height: '120px',
                                      objectFit: 'cover',
                                      cursor: 'pointer',
                                    }}
                                    onClick={() => window.open(url, '_blank')}
                                  />
                                  <div className="position-absolute top-0 end-0 p-1">
                                    <Button
                                      color="danger"
                                      size="sm"
                                      className="rounded-circle p-1"
                                      style={{ width: '28px', height: '28px' }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteImage(index, true);
                                      }}
                                    >
                                      <FeatherIcon icon="trash-2" size={12} />
                                    </Button>
                                  </div>
                                  <div className="position-absolute bottom-0 start-0 end-0 p-1">
                                    <Button
                                      color="light"
                                      size="sm"
                                      className="w-100 opacity-75"
                                      onClick={() => window.open(url, '_blank')}
                                    >
                                      <FeatherIcon
                                        icon="download"
                                        size={12}
                                        className="me-1"
                                      />
                                      {t('calibration.detail.modals.download', 'Download')}
                                    </Button>
                                  </div>
                                </motion.div>
                              </Col>
                            ))}
                        </Row>
                      </div>
                    )}

                    {/* Documents Section */}
                    {addData.documents.filter((doc) => doc !== '').length >
                      0 && (
                      <div className="mb-4">
                        <h6 className="fw-semibold mb-3">
                          <FeatherIcon
                            icon="file-text"
                            size={16}
                            className="me-2"
                          />
                          {t('shared.documents', 'Documents')} ({addData.documents.filter((doc) => doc !== '').length})
                        </h6>
                        <div className="documents-list">
                          {addData.documents
                            .filter((doc) => doc !== '')
                            .map((url, index) => (
                              <motion.div
                                key={index}
                                className="d-flex align-items-center p-3 bg-light rounded mb-2"
                                whileHover={{ backgroundColor: '#e9ecef' }}
                                transition={{ duration: 0.2 }}
                              >
                                <div
                                  className="bg-white rounded d-flex align-items-center justify-content-center me-3"
                                  style={{ width: '40px', height: '40px' }}
                                >
                                  <FeatherIcon
                                    icon={getDocumentIcon(url)}
                                    size={20}
                                    className="text-primary"
                                  />
                                </div>
                                <div className="flex-grow-1 overflow-hidden">
                                  <p className="mb-0 fw-medium text-truncate">
                                    {getFileNameFromUrl(url)}
                                  </p>
                                  <small className="text-muted">
                                    {url.split('.').pop()?.toUpperCase()}{' '}
                                    {t('shared.document', 'Document')}
                                  </small>
                                </div>
                                <div className="d-flex gap-2">
                                  <Button
                                    color="outline-primary"
                                    size="sm"
                                    onClick={() => window.open(url, '_blank')}
                                  >
                                    <FeatherIcon icon="download" size={14} />
                                  </Button>
                                  <Button
                                    color="outline-danger"
                                    size="sm"
                                    onClick={() =>
                                      handleDeleteDocument(index, true)
                                    }
                                  >
                                    <FeatherIcon icon="trash-2" size={14} />
                                  </Button>
                                </div>
                              </motion.div>
                            ))}
                        </div>
                      </div>
                    )}

                    {/* Empty State */}
                    {!addData.images.filter((img) => img !== '').length &&
                      !addData.documents.filter((doc) => doc !== '').length && (
                        <div className="text-center py-4 bg-light rounded">
                          <div
                            className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                            style={{
                              width: '64px',
                              height: '64px',
                              backgroundColor: '#f8f9fa',
                            }}
                          >
                            <FeatherIcon
                              icon="paperclip"
                              size={24}
                              className="text-muted"
                            />
                          </div>
                          <h6 className="text-muted">{t('shared.no_media', 'No media files')}</h6>
                          <p className="text-muted mb-0">
                            {t('shared.upload_instructions', 'Upload images or documents using the button above')}
                          </p>
                        </div>
                      )}
                  </div>
                </Col>
              </Row>

              {apiError && (
                <div className="alert alert-danger mb-3">{apiError}</div>
              )}
            </form>
          </div>

          <div className="modal-footer bg-light border-top-0">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={addModal.close}
            >
              {t('shared.cancel', 'Cancel')}
            </button>
            <button
              type="button"
              className="btn btn-success"
              onClick={handleCreate}
            >
              {t('calibration.detail.modals.button_create', 'Create Calibration')}
            </button>
          </div>
        </motion.div>
      </Modal>

      {/* Work Order Details Modal */}
      <WorkOrderDetailsModal
        isOpen={workOrderModal.isOpen}
        toggle={workOrderModal.close}
        workOrder={workOrderModal.data}
        workType="calibration"
        onEdit={(workOrder) => {
          workOrderModal.close();
          handleEdit(workOrder);
        }}
      />

      {/* Export Modal */}
      <FileModal
        title={t('calibration.detail.modals.export_title', 'Calibration Work Orders')}
        isOpen={fileModal}
        toggle={() => setFileModal(false)}
        data={calibration}
        onImport={handleImport}
        onExport={handleExport}
        importLabel={t('calibration.detail.modals.import_button_label', 'Import Calibration from Excel')}
        importNeed={false}
        exportLabel={t('calibration.detail.modals.export_button_label', 'Export Calibration to Excel')}
      />
    </div>
  );
};

export default CalibrationDetail
